import { z } from 'zod';
import {
  ManipulationActionSchema,
  ManipulationCreateInputSchema,
  ManipulationFieldSchema,
  ManipulationRuleSchema,
  ManipulationTargetSchema,
  ManipulationTypeSchema,
  ManipulationUpdateInputSchema,
  type ManipulationCreateInput,
  type ManipulationUpdateInput,
} from '@sbclite/core/schemas';
import type { Sip } from '@sbclite/core';

export const MANIPULATION_TARGET_OPTIONS = [
  { value: 'From', label: 'From', description: 'Caller identity header' },
  { value: 'To', label: 'To', description: 'Called party header' },
  { value: 'Request-URI', label: 'Request-URI', description: 'SIP request target' },
  { value: 'P-Asserted-Identity', label: 'P-Asserted-Identity', description: 'Trusted caller ID' },
  { value: 'Remote-Party-ID', label: 'Remote-Party-ID', description: 'Remote party info' },
  { value: 'Contact', label: 'Contact', description: 'Contact address' },
  { value: 'Via', label: 'Via', description: 'Request route' },
] satisfies ReadonlyArray<{ value: z.infer<typeof ManipulationTargetSchema>; label: string; description: string }>;

export const MANIPULATION_FIELD_OPTIONS = [
  { value: 'user', label: 'User Part', description: 'The username portion (e.g., +1234567890)' },
  { value: 'host', label: 'Host Part', description: 'The domain portion (e.g., example.com)' },
  { value: 'display', label: 'Display Name', description: 'The quoted display name' },
  { value: 'full', label: 'Full Header', description: 'The entire header value' },
] satisfies ReadonlyArray<{ value: z.infer<typeof ManipulationFieldSchema>; label: string; description: string }>;

export const MANIPULATION_ACTION_OPTIONS = [
  { value: 'replace', label: 'Replace', description: 'Replace pattern matches with value' },
  { value: 'prefix', label: 'Prefix', description: 'Add value before the content' },
  { value: 'suffix', label: 'Suffix', description: 'Add value after the content' },
  { value: 'strip', label: 'Strip', description: 'Remove pattern matches' },
  { value: 'set', label: 'Set', description: 'Replace entire content with value' },
] satisfies ReadonlyArray<{ value: z.infer<typeof ManipulationActionSchema>; label: string; description: string }>;

export const MANIPULATION_TYPE_OPTIONS = [
  { value: 'header', label: 'Header', description: 'Apply to a SIP header value' },
  { value: 'uri', label: 'Request-URI', description: 'Rewrite the SIP Request-URI used for routing' },
] satisfies ReadonlyArray<{ value: z.infer<typeof ManipulationTypeSchema>; label: string; description: string }>;

const ruleFormSchema = ManipulationRuleSchema.safeExtend({
  description: z.string(),
  hasCondition: z.boolean(),
  condition: z.object({
    target: ManipulationTargetSchema,
    field: ManipulationFieldSchema,
    pattern: z.string(),
  }).optional(),
}).superRefine((rule, ctx) => {
  if (rule.hasCondition && !rule.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Condition is required',
      path: ['condition'],
    });
  }
});

export const manipulationFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string(),
  enabled: z.boolean(),
  rules: z.array(ruleFormSchema),
});

export type ManipulationFormData = z.infer<typeof manipulationFormSchema>;
export type ManipulationRuleFormData = ManipulationFormData['rules'][number];

export function createEmptyManipulationRule(priority: number): ManipulationRuleFormData {
  return {
    priority,
    type: 'header',
    target: 'From',
    field: 'user',
    action: 'replace',
    pattern: '',
    value: '',
    description: '',
    enabled: true,
    hasCondition: false,
  };
}

export function buildManipulationDefaultValues(profile?: Sip.ManipulationInfo | null): ManipulationFormData {
  return {
    name: profile?.name ?? '',
    description: profile?.description ?? '',
    enabled: profile?.enabled ?? true,
    rules: profile?.rules.map((rule) => ({
      priority: rule.priority,
      type: rule.type,
      target: rule.target,
      field: rule.field,
      action: rule.action,
      pattern: rule.pattern,
      value: rule.value,
      description: rule.description ?? '',
      enabled: rule.enabled ?? true,
      condition: rule.condition,
      hasCondition: rule.condition !== undefined,
    })) ?? [],
  };
}

export function reprioritizeManipulationRules(rules: ManipulationRuleFormData[]): ManipulationRuleFormData[] {
  return rules.map((rule, index) => ({ ...rule, priority: (index + 1) * 10 }));
}

export function manipulationFormToCreatePayload(data: ManipulationFormData): ManipulationCreateInput {
  return ManipulationCreateInputSchema.parse({
    name: data.name,
    description: data.description.trim() === '' ? undefined : data.description,
    enabled: data.enabled,
    rules: data.rules.map((rule) => ManipulationRuleSchema.parse({
      priority: rule.priority,
      type: rule.type,
      target: rule.target,
      field: rule.field,
      action: rule.action,
      pattern: rule.pattern,
      value: rule.value,
      description: rule.description.trim() === '' ? undefined : rule.description,
      enabled: rule.enabled,
      condition: rule.hasCondition ? rule.condition : undefined,
    })),
  });
}

export function manipulationFormToUpdatePayload(data: ManipulationFormData): ManipulationUpdateInput {
  return ManipulationUpdateInputSchema.parse({
    ...manipulationFormToCreatePayload(data),
    description: data.description.trim() === '' ? null : data.description,
  });
}
